import React, { useState, useEffect, useRef, useMemo } from "react";
import apiRoutes from "../../api/apiRoutes";
import { appFetchJson } from "../../utils/http";

function useDebounced(v, delay = 250) {
  const [val, setVal] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setVal(v), delay);
    return () => clearTimeout(t);
  }, [v, delay]);
  return val;
}

export default function ComboMultiSelectClientes({
  label = "",
  items = [],
  loading: loadingProp = false,
  selected = [],
  onChange = () => {},
}) {
  const boxRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const deb = useDebounced(input, 250);

  // NUEVO: CONSULTAR A LA API EN TIEMPO REAL
  useEffect(() => {
    const fetchClientes = async () => {
      setLoading(true);
      try {
        const url = apiRoutes.clientes.autocompleteUrl({
          q: deb,
          size: 100,
          page: 0,
          browse: true,
          tipo: "cliente",
          estadoId: 11  // <--- FILTRAR CLIENTES ACTIVOS
        });

        const response = await appFetchJson(url);
        const data = response?.items || [];
        
        setFilteredItems(data.map((c) => ({
          id: c.id,
          codigo: c.codigo,
          nombre: c.nombre,
        })));
      } catch (e) {
        console.error("Error cargando clientes:", e);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchClientes();
    }
  }, [deb, open]);

  const toggle = (id) => {
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];
    onChange(next);
  };

  const marcarTodas = () => {
    const all = filteredItems.map((c) => c.id);
    onChange(Array.from(new Set([...selected, ...all])));
  };

  const desmarcarTodas = () => {
    onChange(selected.filter((id) => !filteredItems.some((c) => c.id === id)));
  };

  const labelText =
    selected.length === 0
      ? "Seleccionar"
      : selected.length === 1
      ? "1 seleccionado"
      : `${selected.length} seleccionados`;

  useEffect(() => {
    const onDoc = (ev) => {
      if (boxRef.current && !boxRef.current.contains(ev.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={boxRef} className="relative w-full">
      {label && <label className="label-strong">{label}</label>}

      <button
        type="button"
        className="input w-full flex items-center text-left pl-3 pr-2"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="truncate">{labelText}</span>
        <span className="ml-auto">▾</span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-80 flex flex-col overflow-hidden">
          <div className="p-2 border-b bg-gray-50 flex flex-wrap items-center gap-2 shrink-0 z-10">
            <input
              placeholder="Filtrar registro"
              className="flex-1 border rounded px-2 py-1"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              autoFocus
            />
            <span className="text-sm text-gray-600">Opciones:</span>
            <button
              type="button"
              onClick={marcarTodas}
              className="text-green-700 text-sm hover:underline"
            >
              ✓ Marcar todas
            </button>
            <button
              type="button"
              onClick={desmarcarTodas}
              className="text-green-700 text-sm hover:underline"
            >
              ✗ Desmarcar todas
            </button>
          </div>

          <div className="flex-1 overflow-auto">
            {loading && (
              <div className="px-3 py-2 text-sm text-gray-500">Cargando…</div>
            )}

            {!loading && filteredItems.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500">
                Sin resultados
              </div>
            )}

            {!loading &&
              filteredItems.map((c) => {
                const checked = selected.includes(c.id);
                return (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(c.id)}
                    />
                    {c.codigo && (
                      <span className="text-gray-500">{c.codigo}</span>
                    )}
                    <span className="truncate">{c.nombre}</span>
                  </label>
                );
              })}
          </div>

          <div className="p-2 border-t text-right shrink-0 bg-white">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-green-700 hover:text-green-800"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}